/**
 * ORCHID New York — the signature purchase interaction.
 *
 * Ported from the approved prototype:
 *   website-design-studio/src/routes/index.tsx              -> TierPanel      (step one)
 *   website-design-studio/src/components/site/TableStep.tsx -> "Your table"   (step two)
 *   website-design-studio/src/lib/zip.ts                    -> the ZIP gate
 *
 * The flow, unchanged from the design Meshilem signed off on:
 *   Select on a tier card opens a centred box ON THE PAGE. It never navigates.
 *   Subscribe / One-time trial adds to the bag and the SAME box turns into "Your table".
 *   An optional vase — opt-in per trigger — rides along in that same add as a second line item.
 *   Continue to checkout stays disabled until the Brooklyn ZIP is eligible.
 *
 * WHY NATIVE <dialog> AND NOT PRESTIGE'S <dialog-element> / <x-modal>
 * ------------------------------------------------------------------
 * Prestige's DialogElement (assets/theme.js) is a shadow-DOM custom element: it renders through a
 * `modal-default-template` in snippets/shadow-dom-templates.liquid, pulls its animation controls
 * out of vendor.min.js, and brings Prestige's own panel chrome, max-widths and close button with
 * it. Reproducing the approved box inside it would mean overriding that chrome from the outside
 * and depending on Prestige internals that a theme update can move. Native <dialog>.showModal()
 * gives us the four things we actually need — top layer (no clipping, no z-index), ::backdrop,
 * a real focus trap, and Esc — with zero dependencies, and leaves the 500ms
 * cubic-bezier(.32,.72,0,1) rise entirely ours to control in orchid-chooser.css.
 *
 * The one thing showModal() does NOT do is lock the page behind it, so we borrow Prestige's own
 * `html.lock` class (theme.css) rather than inventing a second mechanism — and only remove it if
 * we were the ones who added it, so we can never unlock a Prestige drawer out from under itself.
 *
 * No image is ever expressed as a data: URI in this file. Content filters optimise images in
 * flight, including ones they find inside .js, and a rewritten data URI closes the surrounding
 * string and takes the whole script down — invisible on an unfiltered machine.
 */
(function () {
  'use strict';

  if (window.orchidChooserLoaded) return;
  window.orchidChooserLoaded = true;

  var SELECTED_DEFAULT = 'Selected';
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Brooklyn ZIP ---------------------------------------------------
   *
   * The BINDING rule is the checkout validation Shopify Function in milestone 3. This is only the
   * courtesy answer before anyone reaches checkout: it must never be the only thing standing
   * between a customer and a sale, and it never returns a hard refusal — anything off-route is a
   * waitlist message.
   *
   * Kept byte-identical to checkZip() in assets/orchid.js so the box and the page-level ZIP check
   * can never disagree. (The React prototype's src/lib/zip.ts used a slightly different core set
   * and a 9:00–12:00 window; the theme's copy — a 9:00 AM to 1:00 PM window — is the later one and
   * is what the client has seen. If that ever changes, change it in both files.)
   */
  var CORE = ['11219', '11204', '11218', '11230', '11232'];

  function checkZip(value) {
    var zip = (value || '').replace(/\D/g, '');
    if (zip.length === 0) {
      return { status: 'idle', message: 'Enter your ZIP to confirm Friday delivery.' };
    }
    if (zip.length !== 5) {
      return { status: 'invalid', message: 'Five digits — your delivery ZIP.' };
    }
    if (CORE.indexOf(zip) !== -1) {
      return { status: 'eligible', message: 'On the route — Friday, 9:00 AM to 1:00 PM.' };
    }
    if (zip.indexOf('112') === 0) {
      return { status: 'eligible', message: 'On the Brooklyn route — Friday morning.' };
    }
    return {
      status: 'waitlist',
      message: 'We deliver across Brooklyn only for now.'
    };
  }

  /* --- Money ---------------------------------------------------------- */

  function withCommas(number) {
    return number.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
  }

  // Renders cents through the shop's own money_format, so currency and separators are the store's
  // rather than a guess. Supports the four placeholders Shopify emits.
  function formatMoney(cents, format) {
    var value = Number(cents);
    if (!isFinite(value)) return '';
    var pattern = format || '${{amount}}';
    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, name) {
      var amount;
      switch (name) {
        case 'amount_no_decimals':
          return withCommas(String(Math.round(value / 100)));
        case 'amount_with_comma_separator':
          amount = (value / 100).toFixed(2).replace('.', ',');
          return amount.replace(/(\d)(?=(\d\d\d)+,)/g, '$1.');
        case 'amount_no_decimals_with_comma_separator':
          return String(Math.round(value / 100)).replace(/(\d)(?=(\d\d\d)+$)/g, '$1.');
        default:
          return withCommas((value / 100).toFixed(2));
      }
    });
  }

  // The same, with the cents dropped when they are zero: "$58", not "$58.00". Used only for the
  // quiet "Or try once for $58" line (the client's wording, 2026-09-10); the pill keeps the full
  // figure. Odd cents keep their decimals, so $57.50 can never be shown as $57.
  function formatMoneyCompact(cents, format) {
    var value = Number(cents);
    if (!isFinite(value) || Math.round(value) % 100 !== 0) return formatMoney(cents, format);
    var pattern = (format || '${{amount}}')
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, '{{amount_no_decimals_with_comma_separator}}')
      .replace(/\{\{\s*amount\s*\}\}/g, '{{amount_no_decimals}}');
    return formatMoney(cents, pattern);
  }

  /* --- Small helpers --------------------------------------------------- */

  // data-* flags arrive as strings, and a Liquid checkbox renders "false" — which is truthy in JS
  // and is exactly how an opt-in becomes an opt-out-by-accident.
  function isOn(value) {
    if (value === undefined || value === null) return false;
    var v = String(value).trim().toLowerCase();
    return v !== '' && v !== 'false' && v !== '0' && v !== 'no' && v !== 'off';
  }

  function setText(el, text) {
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
  }

  // A footnote element carries its subscription wording (data-footnote-sub) and its one-time
  // wording (data-footnote-once). The text the snippet rendered is the subscription one, so a
  // missing attribute falls back to that rather than to nothing.
  function readWordings(el) {
    if (!el) return { sub: '', once: '' };
    var sub = el.getAttribute('data-footnote-sub');
    if (sub === null) sub = el.textContent.trim();
    var once = el.getAttribute('data-footnote-once');
    if (once === null) once = sub;
    return { sub: sub, once: once };
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var error = new Error((data && (data.description || data.message)) || 'Cart request failed.');
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  }

  /* --- Telling the rest of the page the cart moved ----------------------
     The chooser used to add to the cart and say nothing. Prestige's drawer and
     its <cart-count> badge only re-render when they hear cart:change, so a
     shopper who bought from the modal, closed it and tapped the bag was shown
     an empty cart until the next page load — the purchase had gone through and
     the site was denying it.

     The drawer listens for cart:change unconditionally and then reads
     cart.sections[itsId], so firing the event WITHOUT having asked for its
     section makes Prestige dereference undefined and throw inside its own
     replaceContent. So we ask first, exactly as orchid-cart.js does, and only
     send `sections` when something actually registered. */
  function bundledSections() {
    var ask = { sections: [] };
    try {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: ask })
      );
    } catch (error) {
      /* A theme without the drawer simply gets nothing back. */
    }
    return ask.sections.filter(function (id, i) {
      return id && ask.sections.indexOf(id) === i;
    });
  }

  function withSections(body) {
    var bundled = bundledSections();
    if (bundled.length) {
      body.sections = bundled.join(',');
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  function announceCart(cart) {
    if (!cart) return cart;
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'orchid-chooser:change', cart: cart }
      })
    );
    return cart;
  }


  /* --- New York time -------------------------------------------------------
   *
   * Yom Tov capsules close at the minute (milestone 7): reservations run through 23:59:59
   * America/New_York on a trigger's data-closes-on date, and an upcoming capsule opens at 00:00
   * New York time on its data-opens-on date. These two helpers are the one place that turns such
   * a date into an instant. They are exposed as window.orchidNYEndOfDay / orchidNYStartOfDay for
   * sections/orchid-holiday.liquid, whose inline script keeps a byte-for-byte fallback copy
   * between the same ny-time markers in case this file has not loaded. Keep the two identical.
   */
  /* ny-time:start */
  var nyFormat = null;

  // Epoch ms of a New York wall-clock time on a YYYY-MM-DD date, or NaN when the date is not a
  // real one or the browser cannot do time zones. New York is UTC-4 (EDT) or UTC-5 (EST): try
  // both, and keep the one whose New York wall clock reads back exactly what was asked for, so a
  // DST change is never guessed at.
  function nyWallClock(iso, hour, minute, second) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').replace(/^\s+|\s+$/g, ''));
    if (!m) return NaN;
    try {
      if (!nyFormat) {
        nyFormat = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/New_York',
          hourCycle: 'h23',
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric'
        });
      }
      var offsets = [4, 5];
      for (var i = 0; i < offsets.length; i++) {
        var ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), hour + offsets[i], minute, second);
        var parts = nyFormat.formatToParts(new Date(ms));
        var p = {};
        for (var j = 0; j < parts.length; j++) p[parts[j].type] = parts[j].value;
        // An engine that ignores hourCycle answers in 12-hour time with a day period.
        var period = p.dayPeriod || p.dayperiod;
        var h = Number(p.hour);
        if (period) h = (h % 12) + (/p/i.test(period) ? 12 : 0);
        if (
          Number(p.year) === Number(m[1]) &&
          Number(p.month) === Number(m[2]) &&
          Number(p.day) === Number(m[3]) &&
          h % 24 === hour &&
          Number(p.minute) === minute &&
          Number(p.second) === second
        ) {
          return ms;
        }
      }
    } catch (error) {
      return NaN;
    }
    return NaN;
  }

  // The last millisecond of 23:59:59 New York time: reservations run through the whole day.
  function nyEndOfDay(iso) {
    var t = nyWallClock(iso, 23, 59, 59);
    return isNaN(t) ? NaN : t + 999;
  }

  function nyStartOfDay(iso) {
    return nyWallClock(iso, 0, 0, 0);
  }
  /* ny-time:end */

  var MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // "2026-09-05" -> "September 5". A calendar date, so no time zone is involved.
  function monthDay(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    return m ? MONTH_NAMES[Number(m[2]) - 1] + ' ' + Number(m[3]) : String(iso || '');
  }

  /* --- One chooser box -------------------------------------------------- */

  function Chooser(dialog) {
    this.dialog = dialog;
    this.trigger = null;
    this.data = {};
    this.frequency = 'weekly';
    this.lockedByUs = false;

    var q = function (selector) {
      return dialog.querySelector(selector);
    };
    this.el = {
      panel: q('.orchid-chooser__panel'),
      stepChoose: q('[data-orchid-chooser-step="choose"]'),
      stepTable: q('[data-orchid-chooser-step="table"]'),
      media: q('[data-orchid-chooser-media]'),
      image: q('[data-orchid-chooser-image]'),
      persona: q('[data-orchid-chooser-persona]'),
      name: q('[data-orchid-chooser-name]'),
      copy: q('[data-orchid-chooser-copy]'),
      includes: q('[data-orchid-chooser-includes]'),
      frequency: q('[data-orchid-chooser-frequency]'),
      subscribe: q('[data-orchid-chooser-subscribe]'),
      lock: q('[data-orchid-chooser-lock]'),
      trial: q('[data-orchid-chooser-trial]'),
      vaseGroup: q('[data-orchid-chooser-vase-group]'),
      vaseNote: q('[data-orchid-chooser-vase-note]'),
      vaseNoneName: q('[data-orchid-chooser-vase-none-name]'),
      vaseNoneNote: q('[data-orchid-chooser-vase-none-note]'),
      vaseSummary: q('[data-orchid-chooser-vase-summary]'),
      chooseFootnote: q('[data-orchid-chooser-choose-footnote]'),
      tableFootnote: q('[data-orchid-chooser-table-footnote]'),
      chooseError: q('[data-orchid-chooser-error="choose"]'),
      tableError: q('[data-orchid-chooser-error="table"]'),
      editorNote: q('[data-orchid-chooser-editor-note]'),
      lines: q('[data-orchid-chooser-lines]'),
      empty: q('[data-orchid-chooser-empty]'),
      zip: q('[data-orchid-chooser-zip]'),
      zipResult: q('[data-orchid-chooser-zip-result]'),
      subtotal: q('[data-orchid-chooser-subtotal]'),
      checkout: q('[data-orchid-chooser-checkout]')
    };

    this.routes = {
      cart: dialog.getAttribute('data-cart-url') || '/cart',
      add: dialog.getAttribute('data-cart-add-url') || '/cart/add',
      change: dialog.getAttribute('data-cart-change-url') || '/cart/change',
      update: dialog.getAttribute('data-cart-update-url') || '/cart/update',
      checkout: dialog.getAttribute('data-checkout-url') || '/checkout'
    };
    this.moneyFormat = dialog.getAttribute('data-money-format') || '${{amount}}';

    this.subscribable = false;
    this.vaseEnabled = false;
    // Set by the host section (chooser_transactional): step one drops its marketing lines so the
    // pill lands inside the first screen of a phone. Off for every caller that does not ask.
    this.transactional = isOn(dialog.getAttribute('data-transactional'));
    // The snippet's own wording, kept so a tier that overrides it (data-vase-note,
    // data-vase-none-label, data-vase-none-note, data-footnote, data-table-footnote) does not
    // leave its override behind on the next tier the shopper opens.
    this.vaseDefaultNote = this.el.vaseNote ? this.el.vaseNote.textContent : '';
    this.vaseDefaultNoneName = this.el.vaseNoneName ? this.el.vaseNoneName.textContent : '';
    this.vaseDefaultNoneNote = this.el.vaseNoneNote ? this.el.vaseNoneNote.textContent : '';
    this.footnotes = {
      choose: readWordings(this.el.chooseFootnote),
      table: readWordings(this.el.tableFootnote)
    };

    this.bind();
  }

  Chooser.prototype.bind = function () {
    var self = this;
    var dialog = this.dialog;

    // Esc. The native `cancel` event fires before the dialog closes, so we take it over and run
    // the same closing animation as every other exit.
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      self.close();
    });

    // Backdrop. The <dialog> fills the viewport and paints nothing; the box is the child panel, so
    // a click whose target is the dialog itself is a click on the dim.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) self.close();
    });

    dialog.addEventListener('click', function (event) {
      var el = event.target.closest ? event.target.closest('[data-orchid-chooser-close], [data-orchid-chooser-dismiss]') : null;
      if (el) self.close();
    });

    var back = dialog.querySelector('[data-orchid-chooser-back]');
    if (back) {
      back.addEventListener('click', function () {
        self.showStep('choose');
      });
    }

    Array.prototype.forEach.call(dialog.querySelectorAll('[data-orchid-chooser-freq]'), function (button) {
      button.addEventListener('click', function () {
        self.setFrequency(button.getAttribute('data-orchid-chooser-freq'));
      });
    });

    if (this.el.subscribe) {
      this.el.subscribe.addEventListener('click', function () {
        self.addToCart('subscription');
      });
    }
    if (this.el.trial) {
      this.el.trial.addEventListener('click', function () {
        self.addToCart('trial');
      });
    }

    // Vase add-on. Delegated `change` on the group, so the radios can be re-rendered or reordered
    // by the theme editor without re-binding.
    if (this.el.vaseGroup) {
      this.el.vaseGroup.addEventListener('change', function (event) {
        var input = event.target;
        if (!input || !input.hasAttribute || !input.hasAttribute('data-orchid-chooser-vase')) return;
        self.hideError();
        self.renderVase();
        self.renderActions();
      });
    }

    if (this.el.zip) {
      this.el.zip.addEventListener('input', function () {
        self.el.zip.value = self.el.zip.value.replace(/\D/g, '');
        self.renderZip();
      });
    }

    if (this.el.checkout) {
      this.el.checkout.addEventListener('click', function () {
        self.checkout();
      });
    }

    // Line-item steppers and Remove are delegated — the list is re-rendered from the live cart on
    // every change, so per-button listeners would go stale.
    if (this.el.lines) {
      this.el.lines.addEventListener('click', function (event) {
        var button = event.target.closest ? event.target.closest('[data-line-key]') : null;
        if (!button) return;
        self.changeLine(button.getAttribute('data-line-key'), parseInt(button.getAttribute('data-line-qty'), 10));
      });
    }
  };

  /* --- Open / close ----------------------------------------------------- */

  Chooser.prototype.open = function (trigger) {
    // Swapping straight from one tier to another: release the old card before claiming the new
    // one, so two cards can never both read "Selected".
    if (this.trigger && this.trigger !== trigger) this.markTrigger(false);

    var d = {};
    Array.prototype.forEach.call(trigger.attributes, function (attribute) {
      if (attribute.name.indexOf('data-') === 0) d[attribute.name.slice(5)] = attribute.value;
    });
    this.data = d;
    this.trigger = trigger;
    this.frequency = 'weekly';

    this.renderChoose();
    this.showStep('choose', true);
    this.hideError();
    if (this.el.panel) this.el.panel.scrollTop = 0;

    this.dialog.classList.remove('is-closing');
    if (!this.dialog.open) this.dialog.showModal();
    this.lock();
    this.markTrigger(true);
    this.focusFirst();
  };

  // showModal() lands focus on the first focusable thing in the box, which is the close button —
  // a screen-reader user's first word after the tier name would be "Close". Land on the first
  // control that means something instead: the checked frequency, else the (declined) vase, else
  // the buy button. Document order, skipping anything hidden by the milestone-2 gate or the
  // vase opt-in.
  Chooser.prototype.focusFirst = function () {
    var step = this.el.stepChoose;
    if (!step) return;
    var candidates = step.querySelectorAll(
      '[data-orchid-chooser-freq][aria-checked="true"], ' +
        '[data-orchid-chooser-vase]:checked, ' +
        '.orchid-chooser__actions > button'
    );
    for (var i = 0; i < candidates.length; i += 1) {
      var control = candidates[i];
      if (control.hidden || control.disabled) continue;
      if (control.closest && control.closest('[hidden]')) continue;
      control.focus();
      return;
    }
  };

  Chooser.prototype.close = function () {
    var self = this;
    if (!this.dialog.open) return;

    var finish = function () {
      self.dialog.classList.remove('is-closing');
      var returnTo = self.trigger;
      self.dialog.close();
      self.unlock();
      self.markTrigger(false);
      self.trigger = null;
      // <dialog> returns focus to whatever was focused when showModal() ran — which is the wrong
      // card if the shopper swapped tiers while the box was open. Put it back on the card they
      // actually came from.
      if (returnTo && document.contains(returnTo)) returnTo.focus({ preventScroll: true });
    };

    if (reduced) {
      finish();
      return;
    }

    this.dialog.classList.add('is-closing');
    var panel = this.el.panel;
    if (!panel) {
      finish();
      return;
    }
    var done = false;
    var once = function () {
      if (done) return;
      done = true;
      panel.removeEventListener('animationend', once);
      finish();
    };
    panel.addEventListener('animationend', once);
    // Belt and braces: if the animation never fires (display quirks, a background tab) the box
    // must still close.
    window.setTimeout(once, 400);
  };

  // Prestige counts its own lock layers privately, so we only ever add the class when nothing else
  // holds it and only ever remove the one we added.
  //
  // Prestige's `.lock` is `overflow: hidden` on <html> alone, which iOS Safari honours for the
  // wheel and not reliably for a finger — a touch on the dim could still drag the page behind
  // the box (Meshilem's mobile audit, 2026-09-10). `orchid-chooser-open` is ours, always added
  // and always removed, and orchid-chooser.css pins <body> under it as well; it never touches
  // Prestige's count.
  Chooser.prototype.lock = function () {
    var root = document.documentElement;
    root.classList.add('orchid-chooser-open');
    if (root.classList.contains('lock')) return;
    root.classList.add('lock');
    this.lockedByUs = true;
  };

  Chooser.prototype.unlock = function () {
    var root = document.documentElement;
    root.classList.remove('orchid-chooser-open');
    if (!this.lockedByUs) return;
    root.classList.remove('lock');
    this.lockedByUs = false;
  };

  Chooser.prototype.markTrigger = function (open) {
    var trigger = this.trigger;
    if (!trigger) return;
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', this.dialog.id);
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    trigger.classList.toggle('is-chooser-open', open);

    var label = trigger.querySelector('[data-orchid-chooser-label]') || trigger.querySelector('.orchid-btn');
    if (!label) return;
    if (open) {
      if (label.getAttribute('data-orchid-chooser-idle') === null) {
        label.setAttribute('data-orchid-chooser-idle', label.textContent.trim());
      }
      label.textContent = trigger.getAttribute('data-selected-label') || SELECTED_DEFAULT;
    } else {
      var idle = label.getAttribute('data-orchid-chooser-idle');
      if (idle !== null) label.textContent = idle;
    }
  };

  /* --- Steps -------------------------------------------------------------- */

  Chooser.prototype.showStep = function (step, silent) {
    var showing = step === 'choose' ? this.el.stepChoose : this.el.stepTable;
    var hiding = step === 'choose' ? this.el.stepTable : this.el.stepChoose;
    if (hiding) {
      hiding.hidden = true;
      hiding.classList.remove('is-entering');
    }
    if (!showing) return;
    showing.hidden = false;
    if (this.el.panel) this.el.panel.scrollTop = 0;

    // The box TURNS — the step slides in inside the same box rather than the page changing.
    if (!silent && !reduced) {
      showing.classList.remove('is-entering');
      // Force a reflow so the animation restarts on a repeat visit to the same step.
      void showing.offsetWidth;
      showing.classList.add('is-entering');
    }

    if (!silent) {
      showing.setAttribute('tabindex', '-1');
      showing.focus({ preventScroll: true });
    }
  };

  Chooser.prototype.renderChoose = function () {
    var d = this.data;
    var el = this.el;

    if (el.image && el.media) {
      if (d.image) {
        el.image.setAttribute('src', d.image);
        el.image.setAttribute('alt', d['image-alt'] || '');
        el.media.hidden = false;
      } else {
        el.image.removeAttribute('src');
        el.media.hidden = true;
      }
    }

    setText(el.persona, d.persona);
    if (el.name) el.name.textContent = d.name || '';
    // A transactional box has no room for the tier paragraph: the shopper read it on the card
    // they just pressed, and on a 375-wide phone it alone is a screen's worth of scrolling.
    setText(el.copy, this.transactional ? '' : d.copy);
    // The Holiday capsules feed their palette line to both attributes. One paragraph, not two.
    var includes = d.includes || '';
    if (d.copy && includes.trim() === d.copy.trim()) includes = '';
    setText(el.includes, includes);

    // ---- Milestone 2 gate -------------------------------------------------
    // Two selling plans (weekly and bi-weekly) have to exist on the tier product before a
    // subscription can honestly be sold. Until the subscription app is configured we show the
    // trial path only, rather than adding a plain one-off variant and calling it a subscription.
    var missing = [];
    if (!d['sub-variant']) missing.push('data-sub-variant');
    if (!d['selling-plan-weekly']) missing.push('data-selling-plan-weekly');
    if (!d['selling-plan-biweekly']) missing.push('data-selling-plan-biweekly');
    var subscribable = missing.length === 0;
    this.subscribable = subscribable;

    if (el.frequency) el.frequency.hidden = !subscribable;

    // The footnotes follow the gate. "Pause or skip any week" is only true of a subscription, so a
    // trial-only tier, a Yom Tov capsule or a gift gets the one-time wording — unless the trigger
    // brought its own (data-footnote / data-table-footnote), which wins for that tier only.
    // The step-one footnote ("… Checkout is next.") goes with the paragraph in a transactional
    // box. The "Your table" footnote is untouched: that step is where the cutoff matters.
    setText(
      el.chooseFootnote,
      this.transactional ? '' : d.footnote || (subscribable ? this.footnotes.choose.sub : this.footnotes.choose.once)
    );
    setText(el.tableFootnote, d['table-footnote'] || (subscribable ? this.footnotes.table.sub : this.footnotes.table.once));

    if (el.editorNote) {
      // Theme editor only (rendered behind request.design_mode in the snippet).
      if (subscribable) {
        el.editorNote.hidden = true;
      } else {
        el.editorNote.hidden = false;
        el.editorNote.textContent =
          'Subscriptions are not connected yet (milestone 2). The Weekly / Bi-weekly toggle and the ' +
          'Subscribe button are hidden until this card carries: ' + missing.join(', ') + '. ' +
          'The one-time trial works today. Shoppers see no warning — only the trial.';
      }
    }

    this.resetVase();
    this.renderVase();
    this.renderActions();
    this.setFrequency('weekly');
  };

  /* --- The vase add-on ------------------------------------------------------
   *
   * Meshilem's 2026-09-03 ruling: vases are add-ons and bundles only, never a shoppable
   * category. This is the arrangement half of it. Opt-in per trigger, "No vase" pre-selected,
   * and the vase rides along in the SAME /cart/add.js call as the arrangement — one action, two
   * line items, so a shopper can never end up with a vase and no flowers.
   *
   * It is deliberately NOT a third step. The box already turns once (choose -> table); a second
   * turn to ask an optional question would put a wall in front of the purchase for the majority
   * who do not want a vase.
   */

  // The checked vase, or null for "No vase" / the group being off. The DOM is the state — there
  // is no parallel copy of the selection to drift out of sync.
  Chooser.prototype.selectedVase = function () {
    if (!this.vaseEnabled || !this.el.vaseGroup) return null;
    var input = this.el.vaseGroup.querySelector('[data-orchid-chooser-vase]:checked');
    if (!input || !input.value) return null;
    var id = Number(input.value);
    if (!isFinite(id) || id <= 0) return null;
    var price = Number(input.getAttribute('data-vase-price'));
    return {
      id: id,
      price: isFinite(price) ? price : 0,
      name: input.getAttribute('data-vase-name') || 'vase'
    };
  };

  // Declining is the default on every open. Carrying a vase over from the tier the shopper just
  // looked at would be choosing on their behalf.
  Chooser.prototype.resetVase = function () {
    if (!this.el.vaseGroup) return;
    Array.prototype.forEach.call(
      this.el.vaseGroup.querySelectorAll('[data-orchid-chooser-vase]'),
      function (input) {
        input.checked = !input.value;
      }
    );
  };

  Chooser.prototype.renderVase = function () {
    var el = this.el;
    if (!el.vaseGroup) return;

    // A trigger that says nothing about vases gets no vase group — that is the whole backward
    // compatibility guarantee, and it is one line.
    var hasOptions = !!el.vaseGroup.querySelector('[data-orchid-chooser-vase]:not([value=""])');
    this.vaseEnabled = isOn(this.data['vase-step']) && hasOptions;
    el.vaseGroup.hidden = !this.vaseEnabled;

    // Tiers that already ship a vessel say so in their own words (data-vase-note); everyone else
    // gets the snippet's line, which already rules out "one per delivery". The same tiers reword
    // the decline option: "No extra vase / The included welcome vase only" rather than telling a
    // Classic subscriber the stems arrive "ready for a vessel you own" — they were just promised
    // one, in the includes line above.
    if (el.vaseNote) el.vaseNote.textContent = this.data['vase-note'] || this.vaseDefaultNote;
    if (el.vaseNoneName) el.vaseNoneName.textContent = this.data['vase-none-label'] || this.vaseDefaultNoneName;
    if (el.vaseNoneNote) el.vaseNoneNote.textContent = this.data['vase-none-note'] || this.vaseDefaultNoneNote;

    /* The line under the row is now the only place a vessel is described. The
       notes used to sit inside the tiles, where the decline option's sentence
       wrapped to six lines in an 89px column and, because the tiles share a grid
       row, set the height of all four. Moving it here took the group from 292px
       to a fraction of that and brought the buy buttons back above the fold.
       So this must never be empty: whichever option is selected, its own words
       go here. */
    if (!el.vaseSummary) return;
    var vase = this.selectedVase();
    if (!vase) {
      // "The included welcome vase only" is a disclaimer, and a transactional box carries none;
      // the decline option's own sr-only note still tells a screen reader what it declined. A
      // chosen vase keeps its line in every mode — that one states a charge.
      var declineNote = this.transactional ? '' : el.vaseNoneNote ? el.vaseNoneNote.textContent.trim() : '';
      el.vaseSummary.textContent = declineNote;
      el.vaseSummary.hidden = !declineNote;
      return;
    }
    el.vaseSummary.textContent =
      vase.name + ' · ' + formatMoney(vase.price, this.moneyFormat) + ', added once with this order.';
    el.vaseSummary.hidden = false;
  };

  /* --- The two buttons, and the running total --------------------------------
   *
   * The trial button carries the combined figure because a one-time trial plus a one-time vase is
   * one honest number. Subscribe keeps its recurring price alone: a vessel is charged once, and
   * folding it into a per-week price would misstate what the customer is signing up for. The
   * split is spelled out in the summary line above, and step two shows the real cart subtotal
   * from Shopify — never a number this file invented.
   */
  Chooser.prototype.renderActions = function () {
    var d = this.data;
    var el = this.el;
    var vase = this.selectedVase();
    var extra = vase ? vase.price : 0;

    // The reserve shape (Meshilem's final wording, 2026-09-10): ONE pill, "Reserve the Classic —
    // $49.00", and under it ONE quiet text line, "Or try once for $58". Only for a trigger that
    // carries both labels AND can actually be subscribed to, and never for a twin in progress —
    // orchid-referral.css hides the subscribe button there and the one-time pill carries the box,
    // so a text link would leave it with no button. Everything else keeps the old pair exactly.
    var reserve = !!(
      this.subscribable &&
      d['reserve-label'] &&
      d['trial-action-label'] &&
      !this.dialog.classList.contains('is-twin')
    );

    if (el.trial) {
      var base = Number(d['trial-price']);
      var hasTrialPrice = !!d['trial-price'] && isFinite(base);
      var trialLabel;
      if (reserve) {
        var compact = hasTrialPrice ? formatMoneyCompact(base + extra, this.moneyFormat) : '';
        trialLabel = d['trial-action-label'].replace('[price]', compact).replace(/\s+/g, ' ').trim();
        el.trial.className = 'orchid-chooser__try';
      } else {
        trialLabel = d['trial-label'] || 'One-time trial';
        if (hasTrialPrice) trialLabel += ' — ' + formatMoney(base + extra, this.moneyFormat);
        el.trial.className = 'orchid-btn orchid-btn--outline';
      }
      el.trial.textContent = trialLabel;
      el.trial.hidden = !d['trial-variant'];
    }

    if (el.subscribe) {
      el.subscribe.hidden = !this.subscribable;
      var subLabel;
      if (reserve) {
        subLabel = d['reserve-label'];
        if (d['sub-price']) subLabel += ' — ' + formatMoney(d['sub-price'], this.moneyFormat);
      } else {
        subLabel = d['subscribe-label'] || 'Subscribe';
        if (this.subscribable && d['sub-price']) {
          subLabel += ' — ' + formatMoney(d['sub-price'], this.moneyFormat) + ' ' + (d['sub-unit'] || '/ week');
        }
      }
      el.subscribe.textContent = subLabel;
    }

    // The price-lock line belongs to the pill; without the pill there is nothing to lock.
    setText(el.lock, reserve ? d['price-lock-note'] : '');
    this.dialog.classList.toggle('is-reserve', reserve);
  };

  Chooser.prototype.setFrequency = function (frequency) {
    this.frequency = frequency === 'biweekly' ? 'biweekly' : 'weekly';
    var self = this;
    Array.prototype.forEach.call(this.dialog.querySelectorAll('[data-orchid-chooser-freq]'), function (button) {
      var on = button.getAttribute('data-orchid-chooser-freq') === self.frequency;
      button.setAttribute('aria-checked', on ? 'true' : 'false');
      button.setAttribute('tabindex', on ? '0' : '-1');
    });
  };

  /* --- Cart --------------------------------------------------------------- */

  Chooser.prototype.showError = function (step, message) {
    var el = step === 'table' ? this.el.tableError : this.el.chooseError;
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  };

  Chooser.prototype.hideError = function () {
    if (this.el.chooseError) this.el.chooseError.hidden = true;
    if (this.el.tableError) this.el.tableError.hidden = true;
  };

  Chooser.prototype.addToCart = function (kind) {
    var self = this;
    var d = this.data;
    var line;

    if (kind === 'subscription') {
      var plan = this.frequency === 'biweekly' ? d['selling-plan-biweekly'] : d['selling-plan-weekly'];
      if (!d['sub-variant'] || !plan) {
        // Never fake a subscription purchase.
        this.showError('choose', 'Subscriptions are not available yet. The one-time trial is ready now.');
        return;
      }
      line = { id: Number(d['sub-variant']), quantity: 1, selling_plan: Number(plan) };
    } else {
      if (!d['trial-variant']) {
        this.showError('choose', 'This trial is not available right now.');
        return;
      }
      line = { id: Number(d['trial-variant']), quantity: 1 };
    }

    // The vase is a second, plain line in the SAME call. No selling plan on it even when the
    // arrangement has one — a vessel is bought once, not every Friday. /cart/add.js is all-or-
    // nothing across `items`, which is exactly what we want: a failure leaves the cart untouched
    // rather than half-filled.
    // Share the Table: a twin in progress puts its recipient on the line as properties.
    // null means the recipient form did not validate, and nothing is added - the form has
    // already said why. See orchid-referral.js.
    if (window.orchidTwin && window.orchidTwin.active()) {
      var props = window.orchidTwin.properties(this.dialog);
      if (props === null) return;
      line.properties = props;
    }

    var vase = this.selectedVase();
    var items = [line];
    if (vase) items.push({ id: vase.id, quantity: 1 });

    // A Yom Tov capsule closes at the minute (milestone 7). Its trigger carries data-closes-on,
    // and reservations run through 23:59:59 New York time on that date. The card switches itself
    // to closed at that instant, but a box opened before midnight is still on screen after it, so
    // the add is checked here, immediately before it is posted: past the end of that day nothing
    // is posted and the box says why. Subscription, gift and twin triggers carry no
    // data-closes-on and never reach this. An unreadable date blocks nothing; the page's own
    // server-rendered state still stands.
    var closesOn = this.trigger ? this.trigger.getAttribute('data-closes-on') : d['closes-on'];
    if (closesOn) {
      var closedAt = nyEndOfDay(closesOn);
      if (!isNaN(closedAt) && Date.now() > closedAt) {
        this.showError('choose', 'Reservations for ' + (d.name || 'this capsule') + ' closed on ' + monthDay(closesOn) + '.');
        return;
      }
    }

    this.hideError();
    this.el.stepChoose.classList.add('orchid-chooser__busy');

    postJSON(this.routes.add + '.js', withSections({ items: items }))
      .then(function (added) {
        /* /cart/add.js answers with the LINES IT ADDED, not the cart — there is no
           item_count on it. Handing that straight to cart:change made Prestige's
           <cart-count> render the string "undefined" in the bag. So we fetch the
           real cart for the totals and carry the freshly rendered sections across,
           because those are what the drawer redraws itself from. */
        return fetch(self.routes.cart + '.js', { headers: { Accept: 'application/json' } })
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            if (added && added.sections) cart.sections = added.sections;
            return announceCart(cart);
          })
          .catch(function () {
            /* The add itself succeeded; a failed re-read must not read as a failure. */
          });
      })
      .then(function () {
        self.el.stepChoose.classList.remove('orchid-chooser__busy');
        self.showStep('table');
        return self.refresh();
      })
      .catch(function (error) {
        self.el.stepChoose.classList.remove('orchid-chooser__busy');
        var message = error.message || 'We could not add that to your table. Please try again.';
        // Nothing was added, so the shopper needs to know the vase is a way out — but we never
        // silently retry without it and hand them an order they did not choose.
        if (vase) message += ' You can also choose a different vase, or continue without one.';
        self.showError('choose', message);
      });
  };

  Chooser.prototype.changeLine = function (key, quantity) {
    var self = this;
    if (!key || isNaN(quantity)) return;
    this.el.stepTable.classList.add('orchid-chooser__busy');
    postJSON(this.routes.change + '.js', withSections({ id: key, quantity: Math.max(0, quantity) }))
      .then(function (cart) {
        announceCart(cart);
        self.el.stepTable.classList.remove('orchid-chooser__busy');
        self.renderTable(cart);
      })
      .catch(function (error) {
        self.el.stepTable.classList.remove('orchid-chooser__busy');
        self.showError('table', error.message || 'We could not update your table. Please try again.');
      });
  };

  // The table is always rendered from the live cart, never from local state.
  Chooser.prototype.refresh = function () {
    var self = this;
    return fetch(this.routes.cart + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        self.renderTable(cart);
      })
      .catch(function () {
        self.showError('table', 'We could not read your table. Please reload the page.');
      });
  };

  Chooser.prototype.renderTable = function (cart) {
    var self = this;
    var list = this.el.lines;
    if (!list) return;
    this.hideError();
    list.textContent = '';

    var items = (cart && cart.items) || [];
    if (this.el.empty) this.el.empty.hidden = items.length > 0;

    items.forEach(function (item) {
      list.appendChild(self.renderLine(item));
    });

    if (this.el.subtotal) {
      this.el.subtotal.textContent = formatMoney(
        cart && typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : 0,
        this.moneyFormat
      );
    }

    this.itemCount = items.length;
    this.renderZip();
  };

  Chooser.prototype.renderLine = function (item) {
    var self = this;
    var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;

    var li = document.createElement('li');
    li.className = 'orchid-chooser-line';

    var head = document.createElement('div');
    var name = document.createElement('p');
    name.className = 'orchid-chooser-line__name';
    name.textContent = item.product_title || item.title;
    head.appendChild(name);

    var detailParts = [];
    if (plan && plan.name) detailParts.push(plan.name);
    else if (item.variant_title && item.variant_title !== 'Default Title') detailParts.push(item.variant_title);
    if (plan) detailParts.push('billed per delivery');
    if (detailParts.length) {
      var detail = document.createElement('p');
      detail.className = 'orchid-chooser-line__detail';
      detail.textContent = detailParts.join(' · ');
      head.appendChild(detail);
    }
    li.appendChild(head);

    var stepper = document.createElement('div');
    stepper.className = 'orchid-chooser-line__stepper';

    var minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'orchid-chooser-line__step';
    minus.setAttribute('aria-label', 'Decrease ' + (item.product_title || item.title));
    minus.setAttribute('data-line-key', item.key);
    minus.setAttribute('data-line-qty', String(item.quantity - 1));
    minus.textContent = '−';

    var qty = document.createElement('span');
    qty.className = 'orchid-chooser-line__qty';
    qty.textContent = String(item.quantity);

    var plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'orchid-chooser-line__step';
    plus.setAttribute('aria-label', 'Increase ' + (item.product_title || item.title));
    plus.setAttribute('data-line-key', item.key);
    plus.setAttribute('data-line-qty', String(item.quantity + 1));
    plus.textContent = '+';

    stepper.appendChild(minus);
    stepper.appendChild(qty);
    stepper.appendChild(plus);
    li.appendChild(stepper);

    var money = document.createElement('div');
    money.className = 'orchid-chooser-line__money';
    var price = document.createElement('p');
    price.className = 'orchid-chooser-line__price';
    price.textContent = formatMoney(item.final_line_price, self.moneyFormat);
    if (plan) {
      var unit = document.createElement('span');
      unit.className = 'orchid-chooser-line__unit';
      unit.textContent = ' /wk';
      price.appendChild(unit);
    }
    money.appendChild(price);

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'orchid-chooser-line__remove';
    remove.setAttribute('data-line-key', item.key);
    remove.setAttribute('data-line-qty', '0');
    remove.textContent = 'Remove';
    money.appendChild(remove);

    li.appendChild(money);
    return li;
  };

  /* --- ZIP gate + checkout -------------------------------------------------- */

  Chooser.prototype.renderZip = function () {
    if (!this.el.zip || !this.el.zipResult) return;
    var outcome = checkZip(this.el.zip.value);
    this.el.zipResult.textContent = outcome.message;
    this.el.zipResult.classList.toggle('orchid-chooser-zip__result--eligible', outcome.status === 'eligible');
    this.zipStatus = outcome.status;

    if (this.el.checkout) {
      var can = outcome.status === 'eligible' && this.itemCount > 0;
      this.el.checkout.disabled = !can;
      this.el.checkout.setAttribute('aria-disabled', can ? 'false' : 'true');
    }
  };

  Chooser.prototype.checkout = function () {
    if (!this.el.checkout || this.el.checkout.disabled) return;
    var self = this;
    var zip = this.el.zip ? this.el.zip.value : '';
    this.el.checkout.classList.add('orchid-chooser__busy');

    // The courtesy ZIP travels with the cart so the studio has it on the order. The BINDING check
    // is the checkout validation Function in milestone 3, against the real shipping address.
    var go = function () {
      // A twin carries the recipient's address into the checkout's own shipping fields.
      var url = window.orchidTwin ? window.orchidTwin.checkoutUrl(self.routes.checkout) : self.routes.checkout;
      window.location.href = url;
    };
    var attributes = { 'Delivery ZIP': zip };
    // Attribution rides here too, so it survives even when the cart page was never visited.
    try {
      var raw = localStorage.getItem('orchid_ref');
      var v = raw ? JSON.parse(raw) : null;
      if (v && v.ref && v.until > Date.now()) attributes['Referred by'] = String(v.ref);
    } catch (e) { /* no attribution, still a sale */ }
    postJSON(this.routes.update + '.js', { attributes: attributes })
      .then(go)
      .catch(go);
  };

  /* --- Wiring ---------------------------------------------------------------- */

  // The twin form validates a recipient ZIP against the same Brooklyn list, not a copy of it.
  window.orchidCheckZip = checkZip;

  // The holiday page's close-at-the-minute script turns its capsule dates into instants with the
  // same helpers the guard in addToCart uses (it keeps a fallback copy if this file is missing).
  window.orchidNYEndOfDay = nyEndOfDay;
  window.orchidNYStartOfDay = nyStartOfDay;

  var instances = new WeakMap();

  function chooserFor(trigger) {
    // One box per page. The snippet is rendered once; if a page ever renders more than one, a
    // trigger uses the box inside its own section, falling back to the first on the page.
    var section = trigger.closest ? trigger.closest('.shopify-section, section') : null;
    var dialog =
      (section && section.querySelector('[data-orchid-chooser-dialog]')) ||
      document.querySelector('[data-orchid-chooser-dialog]');
    if (!dialog) return null;
    if (!instances.has(dialog)) instances.set(dialog, new Chooser(dialog));
    return instances.get(dialog);
  }

  // Delegated, so cards rendered later by the theme editor need no re-binding.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest ? event.target.closest('[data-orchid-chooser]') : null;
    if (!trigger) return;
    event.preventDefault();
    var chooser = chooserFor(trigger);
    if (!chooser) return;
    if (chooser.dialog.open && chooser.trigger === trigger) {
      // Clicking the open card again closes it, as the prototype's toggle did.
      chooser.close();
    } else {
      // A different card while the box is open re-fills the same box — it never closes and
      // re-opens, which would read as a flicker.
      chooser.open(trigger);
    }
  });
})();
